<script lang="ts" setup>
import { ref, onBeforeUnmount } from 'vue';
import * as Sentry from '@sentry/vue';
import type { QuestionItem } from '@/types';
import PrimeInput from '@/components/UI/PrimeInput.vue';
import PrimeCheckbox from '@/components/UI/PrimeCheckbox.vue';
import PrimeRadio from '@/components/UI/PrimeRadio.vue';
import { useSurveyStore } from '@/stores/survey';
import { storeToRefs } from 'pinia';

const TRACKING_PASTE_QUESTION_ID = import.meta.env
	.VITE_TRACKING_PASTE_QUESTION_ID;

const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const surveyStore = useSurveyStore();

const { valid, isValidationError, errorMessage } = storeToRefs(surveyStore);

const setValidationStatus = surveyStore.setValidationStatus;
const setCurrentQuestion = surveyStore.setCurrentQuestion;
const resetErrorState = surveyStore.resetErrorState;

const props = defineProps<{
	index: number;
	questionItem: QuestionItem;
}>();

const checkedAnswer = ref<string[]>([]);
const checkedAnswers = ref<string[]>([]);
const answerText = ref('');
const hasEverPasted = ref(false);
const pasteSource = ref<
	| 'paste'
	| 'drop'
	| 'input-paste'
	| 'input-drop'
	| 'input-paste-desktop'
	| 'input-drop-desktop'
	| null
>(null);

const MIN_CHARS_FOR_SPEED = 10;
const SPEED_THRESHOLDS = { fast: 600, suspicious: 1500 } as const;
type SpeedCategory = 'normal' | 'fast' | 'suspicious';

const inputStartTime = ref<number | null>(null);
const lastInputTime = ref<number | null>(null);
const hasHighSpeed = ref(false);

const getSpeedCategory = (cpm: number): SpeedCategory => {
	if (cpm > SPEED_THRESHOLDS.suspicious) return 'suspicious';
	if (cpm > SPEED_THRESHOLDS.fast) return 'fast';
	return 'normal';
};

onBeforeUnmount(() => {
	if (
		inputStartTime.value === null ||
		lastInputTime.value === null ||
		!answerText.value
	)
		return;

	const idleBeforeSubmitMs = Date.now() - lastInputTime.value;
	const idleBeforeSubmitSeconds = Math.round(idleBeforeSubmitMs / 1000);

	Sentry.addBreadcrumb({
		category: 'answer.timing',
		message: `Answer submitted after ${idleBeforeSubmitSeconds}s idle since last input`,
		level: 'info',
		data: {
			idleBeforeSubmitSeconds,
			answerLength: answerText.value.length,
		},
	});

	const typingWindowMs = lastInputTime.value - inputStartTime.value;
	const typingElapsedMs = Math.max(typingWindowMs, 100);
	const length = answerText.value.length;

	if (
		hasEverPasted.value &&
		props.questionItem.id === TRACKING_PASTE_QUESTION_ID
	) {
		Sentry.captureMessage('Answer submitted with pasted content', {
			level: 'info',
			tags: {
				'answer.paste_source': pasteSource.value ?? 'unknown',
				'answer.length_range':
					length < 50 ? 'short' : length < 200 ? 'medium' : 'long',
				'answer.idle_before_submit_seconds': idleBeforeSubmitSeconds,
				'answer.typing_seconds': Math.round(typingElapsedMs / 1000),
				'answer.length': length,
			},
			extra: {
				answer: answerText.value,
			},
		});
	}

	if (hasEverPasted.value || typingWindowMs < 500 || length < MIN_CHARS_FOR_SPEED)
		return;

	const cpm = (length / typingElapsedMs) * 60_000;
	const category = getSpeedCategory(cpm);

	if (category === 'normal') return;

	Sentry.captureMessage('Answer submitted with abnormal typing speed', {
		level: category === 'suspicious' ? 'warning' : 'info',
		tags: {
			'answer.speed_category': category,
			'answer.cpm_speed': Math.round(cpm),
			'answer.marked_cp': hasHighSpeed.value && length > 50,
			'answer.typing_seconds': Math.round(typingElapsedMs / 1000),
			'answer.idle_before_submit_seconds': idleBeforeSubmitSeconds,
			'answer.length': length,
		},
		extra: {
			answer: answerText.value,
		},
	});
});

const validate = (value: any): void => {
	if (value) {
		setValidationStatus(true);
	} else {
		setValidationStatus(false);
	}
};

const checkAnswer = (optionId: string): void => {
	checkedAnswer.value = [optionId];

	validate(checkedAnswer.value.length);

	if (valid.value) {
		setCurrentQuestion({
			id: props.questionItem.id,
			answers: checkedAnswer.value,
		});
	}
};

const checkAnswers = (optionId: string, checked: boolean): void => {
	if (checked) {
		checkedAnswers.value.push(optionId);
	} else {
		checkedAnswers.value.splice(checkedAnswers.value.indexOf(optionId), 1);
	}

	validate(checkedAnswers.value.length);

	if (valid.value) {
		setCurrentQuestion({
			id: props.questionItem.id,
			answers: checkedAnswers.value,
		});
	}
};

const PASTE_MESSAGES: Record<string, string> = {
	paste: 'User pasted text into answer field',
	drop: 'User dropped text into answer field',
	'input-paste': 'User pasted text via mobile InputEvent (insertFromPaste)',
	'input-drop': 'User dropped text via mobile InputEvent (insertFromDrop)',
	'input-paste-desktop':
		'User pasted text via desktop InputEvent — clipboard history or extension',
	'input-drop-desktop': 'User dropped text via desktop InputEvent',
};

const onPaste = (
	source: 'paste' | 'input-paste' | 'input-paste-desktop' = 'paste'
): void => {
	hasEverPasted.value = true;
	if (!pasteSource.value) pasteSource.value = source;
	Sentry.addBreadcrumb({
		category: 'answer.paste',
		message: PASTE_MESSAGES[source],
		level: 'info',
		data: {
			questionId: props.questionItem.id,
			isTrackedQuestion: props.questionItem.id === TRACKING_PASTE_QUESTION_ID,
		},
	});
};

const onDrop = (
	source: 'drop' | 'input-drop' | 'input-drop-desktop' = 'drop'
): void => {
	hasEverPasted.value = true;
	if (!pasteSource.value) pasteSource.value = source;
	Sentry.addBreadcrumb({
		category: 'answer.paste',
		message: PASTE_MESSAGES[source],
		level: 'info',
		data: {
			questionId: props.questionItem.id,
			isTrackedQuestion: props.questionItem.id === TRACKING_PASTE_QUESTION_ID,
		},
	});
};

const inputAnswer = (event?: InputEvent): void => {
	if (event?.inputType === 'insertFromPaste' && !hasEverPasted.value) {
		onPaste(isMobileDevice ? 'input-paste' : 'input-paste-desktop');
	} else if (event?.inputType === 'insertFromDrop' && !hasEverPasted.value) {
		onDrop(isMobileDevice ? 'input-drop' : 'input-drop-desktop');
	}

	if (answerText.value && inputStartTime.value === null) {
		inputStartTime.value = Date.now();
	}
	if (answerText.value) {
		lastInputTime.value = Date.now();
	}

	if (isValidationError.value) {
		resetErrorState();
	}

	if (!answerText.value) {
		if (hasEverPasted.value) {
			Sentry.addBreadcrumb({
				category: 'answer.paste',
				message: 'Answer field cleared after paste — paste flag reset',
				level: 'info',
				data: { questionId: props.questionItem.id },
			});
		}
		hasEverPasted.value = false;
		pasteSource.value = null;
		inputStartTime.value = null;
		lastInputTime.value = null;
		hasHighSpeed.value = false;
	}

	validate(answerText.value.trim());

	if (valid.value) {
		if (
			!hasHighSpeed.value &&
			!hasEverPasted.value &&
			inputStartTime.value !== null &&
			lastInputTime.value !== null &&
			answerText.value.length >= MIN_CHARS_FOR_SPEED
		) {
			const elapsedMs = lastInputTime.value - inputStartTime.value;
			if (elapsedMs >= 500) {
				const cpm = (answerText.value.length / elapsedMs) * 60_000;
				if (getSpeedCategory(cpm) !== 'normal') {
					hasHighSpeed.value = true;
				}
			}
		}

		let answer = answerText.value;

		const isPasteDetected =
			hasEverPasted.value &&
			props.questionItem.id === TRACKING_PASTE_QUESTION_ID;
		const isSpeedDetected = hasHighSpeed.value && answerText.value.length > 50;

		if (isPasteDetected || isSpeedDetected) {
			answer = `${answerText.value} /cp`;
		}

		setCurrentQuestion({
			id: props.questionItem.id,
			answers: [answer],
		});
	}
};
</script>

<template>
	<div
		class="step container"
		:data-test-id="`step-questions-type-${questionItem.type}`"
	>
		<h3 class="step__question" data-test-id="step-question">
			{{ questionItem.question }}
		</h3>
		<template v-if="questionItem.type === 'single_choice'">
			<PrimeRadio
				v-for="(answerItem, i) in questionItem.answers"
				:id="answerItem.id"
				:key="answerItem.id"
				:label="answerItem.text"
				:value="answerItem.id"
				name="common"
				:data-test-id="`step-answer-${i + 1}-radio`"
				@change="checkAnswer(answerItem.id)"
			/>
		</template>
		<template v-else-if="questionItem.type === 'multiple_choice'">
			<PrimeCheckbox
				v-for="(answerItem, i) in questionItem.answers"
				:id="answerItem.id"
				:key="answerItem.id"
				:label="answerItem.text"
				:value="answerItem.id"
				:data-test-id="`step-answer-${i + 1}-checkbox`"
				@change="checkAnswers(answerItem.id, $event.target.checked)"
			/>
		</template>
		<PrimeInput
			v-if="questionItem.type === 'input_text'"
			v-model="answerText"
			variant="textarea"
			:placeholder="`${$t('enter_answer')}`"
			:valid="!isValidationError"
			:error-text="errorMessage"
			:data-test-id="`step-answer-input`"
			@input="inputAnswer($event)"
			@paste="onPaste()"
			@drop="onDrop()"
		/>
	</div>
</template>

<style lang="scss">
@import '@/styles/utils/variables.scss';
@import '@/styles/utils/mixins.scss';

.step {
	&__question {
		margin-bottom: 1.5rem;
		font-size: var(--font-20);

		@include breakpoint(0, $md) {
			font-size: var(--font-18);
		}
	}
}
</style>
